import { Button } from '@Pages/pageRuntime/components/ui/Button';
import { useStepper } from '@Pages/pageRuntime/hooks/useStepper';
import { cn } from '@Pages/pageRuntime/lib/utils';
import type React from 'react';
import { Children, Fragment, isValidElement } from 'react';

type StepProps = {
  /** Step label shown in the indicator and the "Step X of N: <label>" caption. */
  label: string;

  /** Fields and content shown when this step is the active one. */
  children?: React.ReactNode;
};

/**
 * A single step inside `Sdk.Components.Stepper`. A marker for the step's label and content;
 * `Stepper` controls which step is visible and renders the indicator and navigation.
 *
 * @example
 * <Sdk.Components.Step label="General">
 *   <FieldInput field={tutors.fields.first_name} />
 *   <FieldInput field={tutors.fields.email} />
 * </Sdk.Components.Step>
 */
export function Step({ children }: StepProps) {
  return <>{children}</>;
}
Step.displayName = 'Step';

type StepperProps = {
  /** `Sdk.Components.Step` children, one per step, in order. */
  children: React.ReactNode;

  /** Index to start on. Defaults to `0`. */
  initialStep?: number;

  /** Label for the back button. Defaults to "Previous". */
  backLabel?: string;

  /** Label for the advance button. Defaults to "Next". */
  nextLabel?: string;

  /** Label for the final-step action button. Defaults to "Submit". */
  submitLabel?: string;

  /**
   * Called when the final step's action button is clicked. When provided, that button is a
   * plain button. When omitted, the final button is `type="submit"`, so an enclosing
   * `Sdk.Components.CreateResourceRecordForm` / `Form` handles submission.
   */
  onComplete?: () => void;

  /** Disables the final-step action button, e.g. while the enclosing form is submitting. */
  isSubmitDisabled?: boolean;

  className?: string;
};

function isStepElement(child: React.ReactNode): child is React.ReactElement<StepProps> {
  return isValidElement(child) && child.type === Step;
}

/**
 * Renders a multi-step / wizard flow: a numbered, clickable step indicator, a
 * "Step X of N: <label>" caption, the active step's content, and Previous/Next navigation.
 * On the last step the Next button becomes a submit (or calls `onComplete`).
 *
 * For multi-step **record forms**, prefer the `steps` prop on
 * `Sdk.Components.CreateResourceRecordForm` / `UpdateResourceRecordForm`, which renders this
 * component internally with submit wiring and an optional review step included. Use `Stepper`
 * directly only for wizards that are not record forms. Navigation is free — moving between
 * steps never validates or blocks.
 *
 * @example
 * const [done, setDone] = React.useState(false);
 *
 * if (done) return <p>You're all set.</p>;
 *
 * return (
 *   <Sdk.Components.Stepper onComplete={() => setDone(true)} submitLabel="Finish">
 *     <Sdk.Components.Step label="Welcome">
 *       <p>Welcome to onboarding.</p>
 *     </Sdk.Components.Step>
 *     <Sdk.Components.Step label="Confirm">
 *       <p>Review the details, then finish.</p>
 *     </Sdk.Components.Step>
 *   </Sdk.Components.Stepper>
 * );
 */
export function Stepper({
  children,
  initialStep,
  backLabel = 'Previous',
  nextLabel = 'Next',
  submitLabel = 'Submit',
  onComplete,
  isSubmitDisabled,
  className
}: StepperProps) {
  const stepElements = Children.toArray(children).filter(isStepElement);

  const labels = stepElements.map((element) => element.props.label);

  const { step, steps, label, goTo, next, back, isFirst, isLast } = useStepper(labels, {
    initialStep
  });

  if (steps === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <ol className="flex items-start">
        {labels.map((stepLabel, index) => {
          const isActive = index === step;
          const isComplete = index < step;
          return (
            <Fragment key={index}>
              <li className="flex shrink-0 flex-col items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => goTo(index)}
                  aria-current={isActive ? 'step' : undefined}
                  className="flex flex-col items-center gap-1.5 focus-visible:outline-none"
                >
                  <span
                    className={cn(
                      'flex size-8 items-center justify-center rounded-full border text-sm font-medium transition-colors',
                      isActive || isComplete
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background text-muted-foreground'
                    )}
                  >
                    {index + 1}
                  </span>
                  <span
                    className={cn(
                      'text-xs',
                      isActive ? 'font-medium text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {stepLabel}
                  </span>
                </button>
              </li>
              {index < steps - 1 ? (
                <span
                  aria-hidden="true"
                  className={cn('mt-4 h-px flex-1', isComplete ? 'bg-primary' : 'bg-border')}
                />
              ) : null}
            </Fragment>
          );
        })}
      </ol>

      <p className="text-sm text-muted-foreground">
        Step {step + 1} of {steps}: {label}
      </p>

      <div>{stepElements[step]}</div>

      <div className="flex items-center justify-between">
        <Button type="button" variant="outline" onClick={back} disabled={isFirst}>
          {backLabel}
        </Button>
        {isLast ? (
          onComplete ? (
            <Button type="button" onClick={onComplete} disabled={isSubmitDisabled}>
              {submitLabel}
            </Button>
          ) : (
            <Button type="submit" disabled={isSubmitDisabled}>
              {submitLabel}
            </Button>
          )
        ) : (
          <Button type="button" onClick={next}>
            {nextLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
